/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BasicAlertComponent } from '../components/alerts/basic-alert.component';
import { XdaStringsService } from '../services/xda-strings.service';
import { MiddlewareService, MWCloudItem } from './middleware.service';

@Component({
  selector: 'middleware-browse',
  templateUrl: './middleware-browse.component.html',
  styleUrls: ['./middleware-browse.component.less'],
  standalone: false,
})
export class MiddlewareBrowseComponent implements OnInit {
  connectorName: string;
  loading = false;
  loadingAll = false;
  enableFilter = false;
  useBrowseById = false;
  enableFileTypes: string;
  multiSelect: boolean;
  maxSelection: number;
  maxSelectionHit = false;
  browsePending = false;
  selectedFile: MWCloudItem; // for single selection
  currentFolder: MWDisplayItem;
  breadcrumb: MWDisplayItem[];
  unfilteredDisplayItems: MWDisplayItem[] | null;
  filterPlaceholder: string;
  private _displayItems: MWDisplayItem[];
  public displayItems: MWDisplayItem[];
  public selectedFilesInFolders: MWSelectedFilesInFolder[] = [];
  returningRepo: MWReturningRepo;

  @Input() sitePath: MWCloudItem;
  @Input() dir: MWCloudItem;
  @Input() returning = false;

  constructor(
    public activeModal: NgbActiveModal,
    private middlewareService: MiddlewareService,
    private modalService: NgbModal,
    private xdaStringsService: XdaStringsService,
  ) {
    this.filterPlaceholder =
      xdaStringsService.strings['SDE_ENTER_SEARCH_TERMS'];
  }

  async ngOnInit() {
    if (this.returning && this.returningRepo) {
      this.middlewareService.region = this.returningRepo.region;
      this.middlewareService.connector = this.returningRepo.connector;
      this.middlewareService.connectorToken = this.returningRepo.accessToken;
      this.middlewareService.connectorRefreshToken =
        this.returningRepo.refreshToken;
      this.sitePath = this.returningRepo.sitePath
        ? this.returningRepo.sitePath[0]
        : { name: '', id: '' };
      this.dir = this.returningRepo.dir
        ? this.returningRepo.dir
        : { name: '', id: '' };
      this.selectedFilesInFolders = this.returningRepo.selectedFolders ?? [];
    } else {
      this.sitePath = { name: '', id: '' };
      this.dir = { name: '', id: '' };
    }

    this.useBrowseById =
      this.middlewareService.connector === 'DocuShare' ? true : false;

    if (
      this.middlewareService.connector == 'GoogleDrive' &&
      this.enableFileTypes?.indexOf('.pdf') >= 0
    ) {
      this.enableFileTypes += ',.googledocs'; // allow .googledocs which download as pdf
    }

    await this.setupHome(false);

    if (this.returning) {
      if (
        this.sitePath.name &&
        this.middlewareService.connector == 'GraphSharePoint'
      ) {
        const sitePathPieces = this.sitePath.name.split('/');
        if (sitePathPieces) {
          sitePathPieces.pop();
          this.sitePath = { name: '', id: '' };

          await this.getSiteOrDirectoryContents(this.currentFolder);
          this.navForward({
            type: 'site',
            glyph: 'xgl-group',
            name: this.xdaStringsService.strings['SDE_TEAM_SITES'],
            ignore: true,
          });
          for (const siteName of sitePathPieces)
            this.navForward({
              id: '',
              type: 'site',
              name: siteName,
              glyph: '',
            });
        }
      }

      if (this.dir.name || this.dir.id) {
        let dirPieces = this.dir.name.split('/');
        if (this.useBrowseById && this.dir.id) {
          // use dir id.
          dirPieces = this.dir.id.split('/');
        }
        if (dirPieces) {
          if (dirPieces[dirPieces.length - 1] === '') {
            dirPieces.pop();
          }

          const browsePiece = dirPieces.pop();
          this.dir = { id: '', name: '' };

          if (this.useBrowseById) {
            await this.setupHome(true);
            for (const dirId of dirPieces) {
              const browseItem = this.displayItems.find((x) => x.id == dirId);
              if (browseItem) {
                await this.browse(browseItem);
              }
            }
            const browseItem = this.displayItems.find(
              (x) => x.id == browsePiece,
            );
            if (browseItem === undefined) {
              this.dir = { id: '', name: '' };
              await this.browse(
                { id: '', type: 'dir', name: '', glyph: '' },
                true,
              ); //initial browse: true
            } else {
              await this.browse(browseItem);
            }
          } else {
            for (const dirName of dirPieces) {
              this.navForward({
                id: '',
                type: 'dir',
                name: dirName,
                glyph: '',
              });
            }

            if (browsePiece === undefined) {
              await this.browse(
                { id: '', type: 'dir', name: '', glyph: '' },
                true,
              ); //initial browse: true
            } else {
              await this.browse(
                { id: '', type: 'dir', name: browsePiece, glyph: '' },
                true,
              );
            }
          }
        }
      } else {
        await this.getSiteOrDirectoryContents(this.currentFolder);
      }
    }
  }

  async setupHome(forceGetDir: boolean) {
    this.currentFolder = this.getLanding(this.middlewareService.connector);
    this.connectorName = this.currentFolder.name;
    this.breadcrumb = [];
    if (forceGetDir || !this.returning) {
      this.loading = this.loadingAll = true;
      await this.getSiteOrDirectoryContents(this.currentFolder);
    }
  }

  getLanding(connector: string): MWDisplayItem {
    let name = '';
    let type = 'land';

    switch (connector) {
      case 'GraphSharePoint':
        name = 'Microsoft® 365 SharePoint';
        break;
      case 'GraphOneDrive':
        name = 'Microsoft® OneDrive';
        type = 'land';
        break;
      case 'GoogleDrive':
        name = 'Google Drive™';
        type = 'dir';
        break;
      case 'DocuShare':
        name = 'DocuShare® Go';
        type = 'dir';
        break;
      case 'Box':
        name = 'Box™';
        type = 'dir';
        break;
      case 'Dropbox':
        name = 'Dropbox™';
        type = 'dir';
        break;
      default:
        name = connector;
        type = 'dir';
        break;
    }
    return { id: '', type: type, glyph: '', name: name };
  }

  async home(): Promise<void> {
    this.sitePath = { name: '', id: '' };
    this.dir = { name: '', id: '' };
    await this.setupHome(true);
  }

  getSelectedFilesInCurrentFolder(initialBrowse = false) {
    if (!this.displayItems) {
      return;
    }

    let selectedFolder = null;
    if (this.selectedFilesInFolders && this.selectedFilesInFolders.length) {
      if (this.useBrowseById) {
        selectedFolder = this.selectedFilesInFolders.find(
          (x) =>
            x.dir.id === this.dir?.id && x.sitePath[0].id === this.sitePath?.id,
        );
      } else {
        selectedFolder = this.selectedFilesInFolders.find(
          (x) =>
            x.dir.name === this.dir?.name &&
            x.sitePath[0].name === this.sitePath?.name,
        );
      }
    }

    // if filter is on then include items in the unfiltered list
    const checkedItems = this.enableFilter
      ? this.unfilteredDisplayItems?.filter((x) => x.checked === true)
      : this.displayItems.filter((x) => x.checked === true);
    if (checkedItems && checkedItems.length) {
      const selectedFiles = checkedItems.map(
        (item) => ({ name: item.name, id: item.id }) as MWCloudItem,
      );
      if (selectedFolder) {
        selectedFolder.files = selectedFiles;
      } else {
        const dir = this.dir
          ? { name: this.dir.name, id: this.dir.id }
          : { name: '', id: '' };
        const sitePath = this.sitePath
          ? { name: this.sitePath.name, id: this.sitePath.id }
          : { name: '', id: '' };
        const sitePaths: MWCloudItem[] = [];
        sitePaths.push(sitePath);
        this.selectedFilesInFolders.push({
          sitePath: sitePaths,
          dir: dir,
          files: selectedFiles,
        });
      }
    } else if (selectedFolder && !initialBrowse) {
      selectedFolder.files = [];
    }
  }

  getTotalSelectedFiles() {
    let totalSelectedFiles = 0;
    if (this.selectedFilesInFolders) {
      this.selectedFilesInFolders.forEach((f) => {
        if (f.files) {
          totalSelectedFiles += f.files.length;
        }
      });
    }
    return totalSelectedFiles;
  }

  back(): void {
    this.loading = this.loadingAll = true;
    //get selected file in the current folder before navigate away
    this.getSelectedFilesInCurrentFolder();
    this.displayItems = [];
    setTimeout(async () => {
      await this.navBack();
    }, 0);
  }

  async navBack(): Promise<void> {
    // Update sitePath and dir
    if (this.currentFolder.type == 'site') {
      const sites = this.sitePath.name.split('/');
      this.sitePath.name = sites.slice(0, sites.length - 2).join('/') + '/';
    } else {
      const dirsId = this.dir.id ? this.dir.id.split('/') : [];
      this.dir.id = dirsId.slice(0, dirsId.length - 2).join('/');
      if (dirsId.length > 2) {
        this.dir.id += '/';
      }

      const dirsName = this.dir.name ? this.dir.name.split('/') : [];
      this.dir.name = dirsName.slice(0, dirsName.length - 2).join('/');
      if (dirsName.length > 2) {
        this.dir.name += '/';
      }
    }

    // update currentFolder and breadcrumb
    this.currentFolder = this.breadcrumb.pop() || this.breadcrumb[0];

    // Update display items
    await this.getSiteOrDirectoryContents(this.currentFolder);
  }

  navForward(item: MWDisplayItem): void {
    // Update breadcrumb and sitePath or dir
    // when we are not ignoring
    this.breadcrumb.push(this.currentFolder);

    if (!item.ignore) {
      if (item.type == 'site') {
        this.sitePath = this.updateSitePathOrDir(this.sitePath, item);
      } else if (item.type == 'dir' || item.type == 'lib') {
        this.dir = this.updateSitePathOrDir(this.dir, item);
      }
    }
    // And current folder
    this.currentFolder = item;
  }

  updateSitePathOrDir(pathOrDir: MWCloudItem, item: MWDisplayItem) {
    if (!pathOrDir) {
      pathOrDir = {
        name: item.name ? item.name + '/' : item.name,
        id: item.id ? item.id + '/' : '',
      };
    } else {
      pathOrDir.name += item.name + '/';
      if (pathOrDir.id) {
        pathOrDir.id += item.id ? item.id + '/' : '';
      } else {
        pathOrDir.id = item.id ? item.id + '/' : '';
      }
    }

    return pathOrDir;
  }

  toggleFilter() {
    // If we are filtering items, set the original items to our unfiltered items so we have something to filter and go back to
    if (!this.unfilteredDisplayItems) {
      this.unfilteredDisplayItems = this.displayItems;
    }

    // If filter is enabled, we are about to disable it, so set display items back to original
    if (this.enableFilter) {
      this._displayItems = this.unfilteredDisplayItems;
      this.loading = this.loadingAll = true;
      this.displayItemsAsync(0);
    }

    this.enableFilter = !this.enableFilter;
  }

  disableFilter() {
    if (this.enableFilter) {
      this._displayItems = this.unfilteredDisplayItems || [];
      this.loading = this.loadingAll = true;
      this.displayItemsAsync(0);
    }

    this.resetFilter();
  }

  resetFilter() {
    this.unfilteredDisplayItems = null;
    this.enableFilter = false;
  }

  filterDisplayItems(filterText: string) {
    if (filterText) {
      this._displayItems =
        this.unfilteredDisplayItems?.filter((displayItem) =>
          displayItem.name.toLowerCase().includes(filterText.toLowerCase()),
        ) || [];
    } else {
      this._displayItems = this.unfilteredDisplayItems || [];
    }

    this.loading = this.loadingAll = true;
    this.displayItemsAsync(0);
  }

  filterHandleEnterKeyPress(event: Event) {
    event.preventDefault();
    this.filterDisplayItems((event.target as HTMLInputElement)?.value);
  }

  async browse(item: MWDisplayItem, initialBrowse = false): Promise<boolean> {
    if (!item) {
      return false;
    }

    //get selected file in the current folder before navigate away
    this.getSelectedFilesInCurrentFolder(initialBrowse);
    this.maxSelectionHit = this.getTotalSelectedFiles() === this.maxSelection;

    if (item.type === 'file') {
      if (this.multiSelect) {
        if (item.selected) {
          item.selected = false;
        } else {
          item.selected = true;
        }
      } else {
        this.selectedFile = { name: item.name, id: item.id || '' };
      }

      return true;
    }

    this.loading = this.loadingAll = true;

    // Disable Filter if it is enabled
    this.resetFilter();

    // clear view
    this.displayItems = [];

    // Navigate forward
    if ((this.useBrowseById && item.id) || (!this.useBrowseById && item.name)) {
      this.navForward(item);
    }

    await this.getSiteOrDirectoryContents(item);
    return true;
  }
  /***
   *  This function is called only in Multiple selection mode
   */
  checkSelectedFiles() {
    if (
      Array.isArray(this.selectedFilesInFolders) &&
      this.selectedFilesInFolders.length
    ) {
      if (this.useBrowseById && this.dir.name === '/') {
        this.dir.name = '';
      }
      const curFolder = this.selectedFilesInFolders.find(
        (file) =>
          file.dir.name === this.dir.name &&
          file.sitePath[0]?.name === this.sitePath?.name,
      );
      if (curFolder) {
        this.displayItems.forEach((item) => {
          if (curFolder.files.find((file) => file.name === item.name)) {
            item.checked = true;
          }
        });
      }
    }
  }

  async getSiteOrDirectoryContents(item: MWDisplayItem) {
    try {
      this._displayItems = [];
      this.browsePending = true;

      if (
        item.type == 'land' &&
        this.middlewareService.connector == 'GraphSharePoint'
      ) {
        this._displayItems = [
          {
            type: 'followed-site',
            glyph: 'xgl-favorites',
            name: this.xdaStringsService.strings['SDE_FOLLOWED_SITES'],
            ignore: true,
          },
          {
            type: 'team-site',
            glyph: 'xgl-group',
            name: this.xdaStringsService.strings['SDE_TEAM_SITES'],
            ignore: true,
          },
        ];
      } else if (item.type == 'followed-site') {
        const sitesList = await this.middlewareService.browse(
          'ListFollowedSites',
          null,
          null,
        );
        if (sitesList != '') {
          const sites = sitesList.split('\n');
          sites.forEach((site) => {
            const siteName = site.substr(0, site.length - 1);
            this._displayItems.push({
              type: 'site',
              glyph: 'xgl-network_square',
              name: siteName,
            });
          });
        }

        this._displayItems.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      } else if (item.type == 'team-site') {
        const sitesList = await this.middlewareService.getSites();
        if (sitesList && sitesList.sites) {
          sitesList.sites.forEach((site) => {
            this._displayItems.push({
              type: 'site',
              glyph: 'xgl-network_square',
              name: site.name,
            });
          });
        }

        this._displayItems.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      } else if (item.type == 'site') {
        const libsList = await this.middlewareService.browse(
          'ListLibraries',
          this.dir,
          this.sitePath,
        );
        const sitesList = await this.middlewareService.browse(
          'ListSubSites',
          this.dir,
          this.sitePath,
        );

        if (libsList != '') {
          const libs = libsList.split('\n');
          libs.forEach((lib) => {
            const libName = lib.substr(0, lib.length - 1);
            this._displayItems.push({
              type: 'lib',
              glyph: 'xgl-accounting',
              name: libName,
            });
          });
        }

        if (sitesList != '') {
          const sites = sitesList.split('\n');
          sites.forEach((site) => {
            const siteName = site.substr(0, site.length - 1);
            this._displayItems.push({
              type: 'site',
              glyph: 'xgl-network_square',
              name: siteName,
            });
          });
        }

        this._displayItems.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      } else {
        this._displayItems = await this.getDirectoryContents();
      }
    } catch (e) {
      if ((e as { message: string; error?: string }).message !== undefined) {
        const error = e as { message: string; error?: string };

        // Caught exception (probably due to missing or invalid token)
        if (error.message == 'ACCESS_TOKEN_REQUIRED') {
          this.activeModal.dismiss(error);
        }

        if (error.error?.length) {
          const modalRef = this.modalService.open(BasicAlertComponent);
          modalRef.componentInstance.title =
            error.error == 'NOT_FOUND'
              ? 'SDE_DESTINATION_NOT_FOUND'
              : 'SDE_SYSTEM_ENCOUNTERED_ERROR1';
          modalRef.result.then(() => {
            if (error.error == 'NOT_FOUND') {
              this.home(); // reset to home if NOT_FOUND, any other error could result in circular error loop trying to get home folder
            }
          });
        }
      }
    }

    this.browsePending = false;
    this.displayItemsAsync(0);

    return;
  }

  // Timer function to display items in chunks so UI doesn't timeout on a large list
  displayItemsAsync(index: number) {
    if (index == 0) {
      this.displayItems = [];
    }
    for (let i = index; i < this._displayItems.length; i++) {
      this.displayItems.push(this._displayItems[i]);

      // If more than 500 items, use timeout to display the rest in chunks of 50 to unblock the UI
      if (this.displayItems.length > 500 && i - index >= 50) {
        this.loading = false; // start showing items instead of "Loading"
        setTimeout(async () => {
          this.displayItemsAsync(i + 1);
        }, 0);

        return;
      }
    }

    // done loading, unless user already selected an item
    if (!this.browsePending) {
      this.checkSelectedFiles();
      this.loading = this.loadingAll = false;
    }
  }

  async getDirectoryContents(): Promise<MWDisplayItem[]> {
    const files: MWDisplayItem[] = [];
    const dirs: MWDisplayItem[] = [];

    if (this.useBrowseById) {
      const folderContents = await this.middlewareService.browseById(
        '',
        '',
        this.currentFolder.id || '',
      );
      if (folderContents) {
        folderContents.files?.forEach((file) => {
          files.push({
            id: file.id,
            type: 'file',
            glyph: 'xgl-file',
            name: file.name,
          });
        });

        folderContents.folders?.forEach((folder) => {
          folder.name = folder.isCollection
            ? this.xdaStringsService.strings[folder.name] || folder.name
            : folder.name;
          dirs.push({
            id: folder.id,
            type: 'dir',
            glyph: folder.isCollection ? 'xgl-copy' : 'xgl-folder',
            name: folder.name,
            readOnly: folder.readOnly,
          });
        });
      }
    } else {
      const dirList = await this.middlewareService.browse(
        'ListDir',
        this.dir,
        this.sitePath,
      );
      if (dirList != '') {
        const dirItems = dirList.split('\n');

        dirItems.forEach((dirItem) => {
          const isFile = dirItem.slice(-1) != '/';
          if (isFile) {
            files.push({ type: 'file', glyph: 'xgl-file', name: dirItem });
          } else {
            const dirName = dirItem.substr(0, dirItem.length - 1);
            dirs.push({ type: 'dir', glyph: 'xgl-folder', name: dirName });
          }
        });
      }
    }

    files.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });
    dirs.sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

    return dirs.concat(files);
  }

  // Used to determine if a folder or file has valid characters (middleware api error's if you try and give it back any files with these characters)
  isValidItem(item: string): boolean {
    const invalidCharacters = new RegExp('[<>\\/""*:]');
    return !invalidCharacters.test(item);
  }

  isFileTypeEnabled(fileName = '') {
    if (!this.enableFileTypes) {
      return false;
    }

    const index = fileName.lastIndexOf('.');
    if (index !== -1) {
      const ext = fileName.substr(index).toLowerCase();
      return this.enableFileTypes.split(',').includes(ext);
    }

    return false;
  }

  isItemEnabled(item: MWDisplayItem) {
    if (item.type == 'file') {
      return this.isFileTypeEnabled(item.name);
    } else if (item.type == 'dir' && !this.enableFileTypes) {
      return !item.readOnly;
    }
    return true;
  }

  // return the selected items.
  selectDirectoryOrFiles() {
    const sitePath: MWCloudItem[] = [];
    sitePath.push(this.sitePath);
    this.activeModal.close({
      connector: this.middlewareService.connector,
      region: this.middlewareService.region,
      sitePath: sitePath,
      dir: this.dir,
      files: this.multiSelect ? this.selectedFilesInFolders : this.selectedFile,
      access: this.middlewareService.connectorToken,
      refresh: this.middlewareService.connectorRefreshToken,
    });
  }

  cancel() {
    this.activeModal.dismiss({
      connector: this.middlewareService.connector,
      region: this.middlewareService.region,
      sitePath: null, // indicate Cancel with null values
      dir: null,
      files: null,
      access: this.middlewareService.connectorToken,
      refresh: this.middlewareService.connectorRefreshToken,
    });
  }
}

export interface MWDisplayItem {
  id?: string;
  type: string;
  glyph: string;
  name: string;
  ignore?: boolean;
  checked?: boolean;
  readOnly?: boolean;
  selected?: boolean;
}

export interface MWSelectedFilesInFolder {
  sitePath: MWCloudItem[];
  dir: MWCloudItem;
  files: MWCloudItem[];
}

export interface MWReturningRepo {
  connector: string;
  region: string;
  accessToken: string;
  refreshToken: string;
  sitePath: MWCloudItem[];
  dir: MWCloudItem;
  selectedFolders: MWSelectedFilesInFolder[];
}
